import { RefObject, useEffect, useRef, useState } from 'react';

interface UseResizeHandlerProps {
  referenceInputRef: RefObject<HTMLDivElement>;
  referenceOutputRef: RefObject<HTMLDivElement>;
  exampleAttachmentsRef: RefObject<HTMLDivElement>;
  rootComponentRef: RefObject<HTMLDivElement>;
  minHeight?: number;
}

interface UseResizeHandlerReturn {
  dragging: boolean;
  handlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerUp: () => void;
}

export function useResizeHandler({
  referenceInputRef,
  referenceOutputRef,
  exampleAttachmentsRef,
  rootComponentRef,
  minHeight = 25,
}: UseResizeHandlerProps): UseResizeHandlerReturn {
  const [dragging, setDragging] = useState(false);
  const handleRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(false);

  // Cleanup function for pointer events
  const cleanup = () => {
    if (isMountedRef.current) setDragging(false);
    handleRef.current?.();
    handleRef.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();

    const initialY = e.clientY;
    const initialHeight = referenceInputRef.current?.clientHeight ?? 200;

    function handlePointerMove(e: PointerEvent) {
      if (!rootComponentRef.current) return;

      const diffY = e.clientY - initialY;
      const maxHeight = rootComponentRef.current.clientHeight - 200;

      // Calculate new height within constraints
      const newHeight = Math.max(
        Math.min(initialHeight + diffY, maxHeight),
        minHeight
      );

      // Apply new height to both reference panes
      referenceInputRef.current?.style.setProperty('height', `${newHeight}px`);
      exampleAttachmentsRef.current?.style.setProperty(
        'height',
        `${newHeight}px`
      );
      referenceOutputRef.current?.style.setProperty('height', `${newHeight}px`);
    }

    function handlePointerUp() {
      cleanup();
    }

    if (isMountedRef.current) setDragging(true);
    handleRef.current?.();

    // Add event listeners for move and up events
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    // Store cleanup function
    handleRef.current = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  };

  const handlePointerUp = () => {
    cleanup();
  };

  // Setup and cleanup effect
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, []);

  return {
    dragging,
    handlePointerDown,
    handlePointerUp,
  };
}
